import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { FileUp } from "lucide-react";
import { toast } from "sonner";

/**
 * Import a recipe from a PDF file via the backend AI pipeline.
 * Props mirror the URL/Image import dialogs for consistency.
 */
export default function PdfImportDialog({ open, onOpenChange, servings, onImported }) {
  const [mealType, setMealType] = useState("dinner");
  const [isBaking, setIsBaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Kun PDF-filer støttes"); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("servings", String(servings));
      formData.append("is_baking", String(isBaking));
      formData.append("meal_type", mealType);
      const res = await api.importPdfRecipe(formData);
      toast.success("PDF-oppskrift importert!");
      onOpenChange(false);
      if (onImported) onImported(res.data?.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved PDF-import");
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] flex flex-col p-0" data-testid="pdf-import-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <FileUp className="h-5 w-5 text-navy-800" />Importer PDF-oppskrift
          </DialogTitle>
          <DialogDescription className="text-xs">Last opp ein PDF med ei oppskrift. AI analyserer innhaldet.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          <div className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Måltidstype</label>
              <Select value={mealType} onValueChange={setMealType}>
                <SelectTrigger data-testid="pdf-meal-type-select"><SelectValue placeholder="Måltidstype" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Frokost</SelectItem>
                  <SelectItem value="lunch">Lunsj</SelectItem>
                  <SelectItem value="dinner">Middag</SelectItem>
                  <SelectItem value="baking">Baking</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer">
              <input type="checkbox" checked={isBaking} onChange={(e) => setIsBaking(e.target.checked)} className="rounded border-stone-300 h-4 w-4" data-testid="pdf-is-baking" />
              Baking-oppskrift
            </label>
            {loading ? (
              <div className="space-y-3 py-4">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <p className="text-xs text-stone-400 text-center mt-2">Analyserer PDF med AI…</p>
              </div>
            ) : (
              <input ref={fileInputRef} type="file" accept=".pdf" onChange={handlePdfImport} className="hidden" data-testid="pdf-file-input" />
            )}
          </div>
        </div>
        {!loading && (
          <div className="border-t border-stone-100 px-5 py-3 bg-white">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-navy-800 px-4 py-3 text-sm font-semibold text-white hover:bg-navy-900 transition-colors"
              data-testid="pdf-upload-btn"
            >
              <FileUp className="h-4 w-4" /> Vel PDF-fil
            </button>
            <p className="text-[11px] text-stone-400 text-center mt-2">Kun .pdf-filer støttast</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
